import { useEffect } from "react";
import { useFormik } from "formik";
import * as yup from "yup";
import { isEmpty } from "lodash";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Button, Divider, Grid2, TextField, Typography } from "@mui/material";
import ArrowForwardOutlinedIcon from '@mui/icons-material/ArrowForwardOutlined';
import { PAGE_LIST } from "../../pages";
import InnerLink from "../../components/InnerLink";
import { login } from "../../store/userSlice/userRequests";
import { selectLoggedIn } from "../../store/userSlice/userSelectors";
import { getQueryObject } from "../../helpers/urlBuilder";
import { loadNextWPPage, removeWPPage } from "../../Routes/RouteToRegister";

import classes from './index.module.scss';

const validationSchema = yup.object({
  username: yup.string().required('Username is required'),
  password: yup.string().required('Password is required'),
})

const LoginContainer = () => {
  const query = getQueryObject();
  const isLoggedIn = useSelector(selectLoggedIn);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const loginFormik = useFormik({
    initialValues: {
      username: '',
      password: '',
    },
    validationSchema,
    onSubmit: () => {},
    enableReinitialize: true,
    validateOnChange: false,
    validateOnBlur: false,
  });

  useEffect(() => {
    if (isLoggedIn) {
      const storageNext = loadNextWPPage();
      if (query.next || storageNext) {
        navigate(decodeURIComponent(query.next || storageNext));
        removeWPPage();
        return;
      }
      navigate(PAGE_LIST.CERTIFICATE);
    }
  }, [isLoggedIn, query]);

  const onSubmit = async () => {
    const errorList = await loginFormik.validateForm();

    if (isEmpty(errorList)) {
      dispatch(login(loginFormik.values))
        .then((action) => {
          const errorText = action?.payload?.message;

          if (errorText) {
            loginFormik.setErrors({
              username: true,
              password: errorText,
            })
          }
        })
    }
  }

  const onKeydown = (event) => {
    if (event.keyCode === 13) {
      onSubmit();
    }
  }

  return (
    <Grid2
      container
      className={classes.loginContainer}
    >
      <Grid2
        container
        direction='column'
        rowSpacing={2}
        className={classes.loginForm}
      >
        <Grid2 size={12}>
          <Typography variant='h1' align='center'>Login</Typography>
        </Grid2>
        <Grid2>
          <Typography variant='body1'>Username</Typography>
          <TextField
            variant='outlined'
            name='username'
            size='medium'
            fullWidth
            className={classes.input}
            value={loginFormik.values?.username}
            onKeyDown={onKeydown}
            onChange={loginFormik.handleChange}
            error={Boolean(loginFormik?.errors?.username)}
          />
        </Grid2>
        <Grid2>
          <Typography variant='body1'>Password</Typography>
          <TextField
            variant='outlined'
            name='password'
            type='password'
            fullWidth
            className={classes.input}
            value={loginFormik.values?.password}
            onKeyDown={onKeydown}
            onChange={loginFormik.handleChange}
            error={Boolean(loginFormik?.errors?.password)}
            helperText={loginFormik?.errors?.password}
          />
        </Grid2>
        <Grid2>
          {/*remember me*/}
        </Grid2>
        <Grid2>
          <Divider />
        </Grid2>
        <Grid2>
          <Button
            variant="contained"
            size="large"
            fullWidth
            className={classes.loginButton}
            onClick={onSubmit}
          >
            Login
          </Button>
        </Grid2>
        <Grid2 container direction='row' className={classes.loginRegisterContainer}>
          <Grid2>
            <Typography variant='body1'>Don't have an account?</Typography>
          </Grid2>
          <Grid2 container className={classes.rightLink}>
            <InnerLink href={PAGE_LIST.REGISTER} color='secondary'>Register</InnerLink>
            <ArrowForwardOutlinedIcon className={classes.loginLinkIcon} />
          </Grid2>
        </Grid2>
        <Grid2 container direction='row' className={classes.loginRegisterContainer}>
          <Grid2>
            <Typography variant='body1'>Forgot your password?</Typography>
          </Grid2>
          <Grid2 container className={classes.rightLink}>
            <InnerLink href={PAGE_LIST.REQUEST_RESET_PASSWORD} color='secondary'>Reset password</InnerLink>
            <ArrowForwardOutlinedIcon className={classes.loginLinkIcon} />
          </Grid2>
        </Grid2>
      </Grid2>
    </Grid2>
  )
}

export default LoginContainer;